// Libs
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useForm, SubmitHandler } from "react-hook-form";
import { z } from "zod";

// Local
import {
  adminPropDefaultsForCustomField,
  CustomFieldManager,
  HoursMinutesCustomField,
} from "../../../models/custom-field";
import {
  TitleField,
  MinField,
  MaxField,
  OnTaskStatusField,
  isDefinedOnTaskStatus,
  HideOnCRCreationField,
  HoursDefaultValueField,
  MinutesDefaultValueField,
} from "../../admin/AddCustomFieldDialog/AddCustomFieldDialog";
import { ErrorMessage } from "../../ErrorMessage";
import { OTaskTypes } from "../../../models/task-types";
import {
  ActionButtons,
  CreateSiteCustomFieldFormInterface,
  HoursMinutesSchema_CSCF,
  RequiredField,
} from "./AddCreateSiteCustomFieldDialog";
import { convertHoursMinutesToDecimal } from "../../../utils";

const hoursMinutesDefaultValues: Partial<HoursMinutesType> = {
  defaultValueHours: null,
  defaultValueMinutes: null,
  min: null,
  max: null,

  title: "",
  editable: true,
  required: false,
  hideOnCraftRecordCreation: false,
};
type HoursMinutesType = z.infer<typeof HoursMinutesSchema_CSCF>;

export const HoursMinutesCreateSiteCustomFieldForm: React.FunctionComponent<
  CreateSiteCustomFieldFormInterface
> = (props) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<HoursMinutesType>({
    defaultValues: hoursMinutesDefaultValues,
    resolver: zodResolver(HoursMinutesSchema_CSCF),
    mode: "onChange",
  });

  useEffect(() => {
    reset(hoursMinutesDefaultValues);
  }, [reset]);

  const [displayError, setDisplayError] = useState(false);
  const [displayWorkTypeError, setDisplayWorkTypeError] = useState(false);

  const onSave: SubmitHandler<HoursMinutesType> = (formValues) => {
    if (props.workType === undefined) {
      setDisplayWorkTypeError(true);
      return;
    }

    // Drop undefined values from the onTaskStatus list.
    // onTaskStatus itself will be undefined if this is a workType.
    const onTaskStatus = formValues.onTaskStatus?.filter(isDefinedOnTaskStatus);

    // Convert hours:minutes to the decimal value.
    let xhours = formValues.defaultValueHours;
    if (typeof xhours !== "number") {
      xhours = 0;
    }
    let xminutes = formValues.defaultValueMinutes;
    if (typeof xminutes !== "number") {
      xminutes = 0;
    }
    const decimal = convertHoursMinutesToDecimal({
      hours: xhours,
      minutes: xminutes,
    });

    // Compose the output structure.
    let fullOutput: HoursMinutesCustomField;

    const partialOutput: Omit<
      HoursMinutesCustomField,
      "taskType" | "craftRecordOrTask"
    > = {
      ...adminPropDefaultsForCustomField(),
      fieldType: "hours-minutes",
      defaultValue: decimal === 0 ? null : decimal,
      title: formValues.title,
      editable: true,
      required: formValues.required,
      min: formValues.min,
      max: formValues.max,
      craftType: props.workType,
    };

    // If taskType is null this means it's a craftRecord, and
    // we don't want to save the taskType to the database.
    if (props.taskType == null) {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "craftRecord",
        hideOnCraftRecordCreation: formValues.hideOnCraftRecordCreation,
      };
    } else {
      fullOutput = {
        ...partialOutput,
        craftRecordOrTask: "task",
        taskType: props.taskType,
        onTaskStatus: onTaskStatus,
      };
    }

    // Validate CustomFieldDocData
    const validatedOutput = CustomFieldManager.parse(fullOutput);
    props.handleSave(validatedOutput);
    props.closeDialog();
  };

  const errorMessage = (
    <ErrorMessage
      message="Something went wrong."
      clearMessage={() => setDisplayError(false)}
    />
  );

  const undefinedWorkTypeErrorMessage = (
    <ErrorMessage
      message="Work Type must be selected"
      clearMessage={() => setDisplayWorkTypeError(false)}
    />
  );

  return (
    <form
      className="contents"
      autoComplete="off"
      onSubmit={handleSubmit(onSave)}
    >
      {props.taskType != null ? (
        <div></div>
      ) : (
        <HideOnCRCreationField control={control} />
      )}

      <TitleField control={control} errors={errors} />

      {/* Specific to fieldType hours-minutes */}
      <HoursDefaultValueField control={control} errors={errors} />
      <MinutesDefaultValueField control={control} errors={errors} />
      <MinField control={control} errors={errors} isStringBased={false} />
      <MaxField control={control} errors={errors} isStringBased={false} />

      <RequiredField control={control} />

      {props.taskType != null &&
        Object.values(OTaskTypes).includes(props.taskType) && (
          <OnTaskStatusField
            taskStatusLabel="Prompt On Task Status"
            control={control}
            taskStatusList={props.taskStatusList}
          />
        )}

      <span className="-my-5 h-10 justify-self-center sm:col-span-2">
        {displayError ? errorMessage : null}
        {displayWorkTypeError ? undefinedWorkTypeErrorMessage : null}
      </span>

      <ActionButtons onCancel={props.closeDialog} />
    </form>
  );
};
